import { Injectable } from '@angular/core';
import { last } from 'lodash';
import { BehaviorSubject, bufferToggle, filter, fromEvent, map, merge, Observable, tap } from 'rxjs';
import { environment } from '../../../environments/environment';

function isEnter(event: KeyboardEvent | undefined) {
  return event?.key === 'Enter';
}

@Injectable({ providedIn: 'root' })
export class BarcodeScannerService {
  private readonly BUFFER_TIME = 300;
  private readonly excludedElements = ['INPUT', 'TEXTAREA'];
  private _scanInProgress = new BehaviorSubject<boolean>(false);

  private _keyEvents: Observable<KeyboardEvent> = fromEvent(document, 'keyup').pipe(
    map(event => event as KeyboardEvent),
    filter((event) => !this.excludedElements.includes((event.target as HTMLElement).tagName)),
    tap(() => {
      if (!this._scanInProgress.getValue()) {
        this._scanInProgress.next(true);
        setTimeout(() => this._scanInProgress.next(false), this.BUFFER_TIME);
      }
    })
  );

  scannerResults: Observable<string>;

  constructor() {
    const start = this._scanInProgress.pipe(filter(v => v));
    const timeout = this._scanInProgress.pipe(filter(v => !v));
    const enter = this._keyEvents.pipe(filter(event => isEnter(event)));
    const stop = merge(timeout, enter);
    // Note: a specific CCV device does not send line feed at the end of scanning sequence, thus we consider every input valid for CCV terminals
    const isValidBarcode = (buffer: KeyboardEvent[]) => environment.runOnAndroidPaymentTerminal || isEnter(last(buffer));

    this.scannerResults = this._keyEvents.pipe(
      bufferToggle(start, () => stop),
      filter(isValidBarcode),
      map(buffer => {
        const adjustedBuffer: string[] = [];
        
        for (let i = 0; i < buffer.length; i++) {
          const event = buffer[i];
          const key = event.key;

          // Skip Enter key
          if (key === 'Enter') {
            continue;
          }

          // If this is a Shift key, check if next key should be shifted
          if (key === 'Shift') {
            const nextEvent = buffer[i + 1];
            if (nextEvent && nextEvent.key !== 'Shift' && nextEvent.key !== 'Enter') {
              const nextKey = nextEvent.key;
              if (nextKey.length === 1) {
                // Apply shift transformation
                adjustedBuffer.push(this.applyShift(nextKey));
                i++; // Skip the next key since we already processed it
              }
            }
            continue;
          }

          // Regular key - only add if it's a single character
          if (key.length === 1) {
            adjustedBuffer.push(key);
          }
        }

        return adjustedBuffer.join('');
      })
    );
  }

  private applyShift(key: string): string {
    // Handle letters
    if (key >= 'a' && key <= 'z') {
      return key.toUpperCase();
    }

    // Handle symbols - US keyboard layout
    const shiftMap: { [key: string]: string } = {
      '1': '!',
      '2': '@',
      '3': '#',
      '4': '$',
      '5': '%',
      '6': '^',
      '7': '&',
      '8': '*',
      '9': '(',
      '0': ')',
      '-': '_',
      '=': '+',
      '[': '{',
      ']': '}',
      '\\': '|',
      ';': ':',
      // eslint-disable-next-line @typescript-eslint/quotes
      "'": '"',
      ',': '<',
      '.': '>',
      '/': '?',
      '`': '~',
    };

    return shiftMap[key] || key;
  }
}
